import Breadcrumbs from 'components/breadcrumbs';
import Headline from 'components/headline';
import config, { useConfig } from 'config';
import { PdEnv } from 'constants/pd-env';
import { getSegmentationFromUrl } from 'constants/segmentation';
import { usePetContext } from 'contexts/pet';
import { useAddItemsByUrl } from 'hooks/cart/use-add-items-by-url';
import { useIsExistingCustomer } from 'hooks/common/use-is-existing-customer';
import useMobile from 'hooks/common/use-mobile';
import { usePreserveScroll } from 'hooks/common/use-preserve-scroll';
import useCollectionReady from 'hooks/gtm/use-collection-ready';
import { useBreadcrumbs } from 'hooks/product/use-breadcrumbs';
import useDynamicTest from 'hooks/test/use-dynamic-ux';
import { possibleTags } from 'interfaces/collection-filter';
import { CollectionProduct } from 'models/collection/collection';
import useTestPT6874 from 'modules/cart/components/tiny-cart/hooks/use-test-pt6874';
import { useContentful } from 'modules/content-cms/components/context';
import dynamic from 'next/dynamic';
import Head from 'next/head';
import { useRouter } from 'next/router';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { jsonLdScriptProps } from 'react-schemaorg';
import { ItemList, ListItem } from 'schema-dts';
import { isGenericCdpPage } from 'utils/api/smartCollection';
import { showSuccessToast } from 'utils/toasts';
import canonical from '../../components/seo/canonical';
import description from '../../components/seo/description';
import image from '../../components/seo/image';
import robots from '../../components/seo/robots';
import pageTitle from '../../components/seo/title';
import seotype from '../../components/seo/type';
import ContentCmsContentStatic from '../content-cms/components/content';
import ContentCmsHeaderContentStatic from '../content-cms/components/header-content';
import ProductsGrid from './components/products-grid';
import ResultCounter from './components/result-counter';
import { useCollectionState } from './state';

const TinyCart = dynamic(() => import('modules/cart/components/tiny-cart'), {
  ssr: false,
});

const VARIANT_SELECTOR_CDP =
  config.pdEnv === PdEnv.PRODUCTION
    ? 'saucen-menu-fur-katzen-bundle-33-bundle-sizes'
    : 'fur-katzen-nassfutter';

const VariantSelectorAsync = dynamic(
  () => import('./components/variant-selector-wrapper')
);

const PetProfileCollectionSelector = dynamic(
  () => import('components/pet-profile/collection-selector')
);

type Input = {
  hasH1title: boolean;
  setApplyPetProfile: (value: boolean) => void;
  handle: string;
  /** Url query to be added to product cart */
  additionalQuery?: Record<string, string>;
};

const HAS_ALLERGY_FILTERS_HANDLE = [
  'fur-hunde-unvertraeglichkeiten-allergien',
  'fur-hunde-unvertraeglichkeiten-allergien-lp',
  'fur-hunde-unvertraeglichkeiten-allergien-lp1',
  'fur-hunde-unvertraeglichkeiten-allergien-lp2',
];
const BLACK_DEALS_HANDLE = [
  'black-weekend-fur-hunde',
  'black-weekend-fur-katzen',
];

const Collection: React.FC<Input> = ({
  hasH1title,
  setApplyPetProfile,
  handle,
  additionalQuery,
}) => {
  const { frontendUrl } = useConfig();
  const { query, isReady } = useRouter();
  const { current } = usePetContext();
  const { collection, visibleProducts } = useCollectionState();
  const { breadcrumbList, isLoading } = useBreadcrumbs({ product: undefined });
  const { loading, showDynamicUx } = useDynamicTest();

  const showPetSelector = useMemo(() => {
    if (current === 'cats') return false;
    if (
      isGenericCdpPage({ url: `/collections/${handle}` }) ||
      getSegmentationFromUrl({ url: `/collections/${handle}` })
    ) {
      return showDynamicUx;
    }
    return false;
  }, [current, handle, showDynamicUx]);
  const { isEc } = useIsExistingCustomer();

  const { data: contentful } = useContentful();

  const shouldShowAllergyFilter = useMemo(
    () =>
      Boolean(
        isReady &&
          query.all?.[0] &&
          HAS_ALLERGY_FILTERS_HANDLE.some(
            (handleName) => handleName === query.all?.[0]
          )
      ),
    [query, isReady]
  );

  // Check if the collection is a black deals collection. Should be removed after the black deals campaign
  const shouldShowBlackRibbon = useMemo(
    () =>
      Boolean(
        isEc &&
          isReady &&
          query.all?.[0] &&
          BLACK_DEALS_HANDLE.some((handleName) => handleName === query.all?.[0])
      ),
    [isEc, isReady, query.all]
  );

  const isMobile = useMobile();

  const shouldInjectVariantSelector = useMemo(
    () => Boolean(isReady && query.all?.[0] === VARIANT_SELECTOR_CDP),
    [query, isReady]
  );

  const location = query.all?.[0] || '';

  const [showTinyCart, setShowTinyCart] = useState(false);
  const { variant: variantTestPT6874 } = useTestPT6874();

  const { addedItems } = useAddItemsByUrl();
  useEffect(() => {
    if (addedItems.length > 0) {
      showSuccessToast({
        message: 'cdp:toast:add-single-item',
      });
      if (variantTestPT6874 !== 0) {
        setShowTinyCart(true);
      }
    }
  }, [addedItems.length, variantTestPT6874]);

  /** Send ready event only once per life cycle, excluding filter updates or SWR update */
  const [shouldTrigger, setShouldTrigger] = useState<boolean>(true);
  /** Temporary selected allergies to be used in the PetProfileCollectionSelector component */
  const [temporarySelectedAllergies, setTemporarySelectedAllergies] = useState<
    possibleTags[] | undefined
  >();
  const handleRef = useRef(false);
  React.useEffect(() => {
    if (handleRef.current) {
      return;
    }
    handleRef.current = true;
    setShouldTrigger(false);
  }, []);

  useCollectionReady({
    shouldTrigger,
    products: collection.products,
    collectionTitle: collection.title,
  });

  usePreserveScroll(!!collection.title);

  const generateItemList = (products: CollectionProduct[]): ListItem[] => {
    const schema: ListItem[] = [];

    products.forEach((product, index) => {
      schema.push({
        '@type': 'ListItem',
        position: index + 1,
        url: `${frontendUrl}/products/${product.handle}`,
      });
    });

    return schema;
  };

  return (
    <>
      {showTinyCart && (
        <TinyCart
          onCloseCallback={() => {
            setShowTinyCart(false);
          }}
        />
      )}
      <Head>
        {pageTitle(
          (contentful && contentful.fields.headline) || collection.title
        )}
        {description(contentful && contentful.fields.metaDescription)}
        {seotype('product.group')}
        {robots(contentful && contentful.fields.metaRobots)}
        {canonical(contentful && contentful.fields.handle)}
        {image(
          contentful && contentful !== null && contentful.fields.metaOgImage
            ? contentful.fields.metaOgImage.fields.file.url
            : undefined
        )}
        <script
          {...jsonLdScriptProps<ItemList>({
            '@context': 'https://schema.org',
            '@type': 'ItemList',
            itemListElement: generateItemList(collection.products),
          })}
        />
      </Head>

      {shouldInjectVariantSelector && isMobile && <VariantSelectorAsync />}

      <ContentCmsHeaderContentStatic priority />

      <div className="bg-other-white px-4 md:px-8">
        <div className="container px-0" id="product-listing-section">
          <Breadcrumbs
            breadcrumbs={breadcrumbList}
            isLoading={isLoading}
            className="mb-3 lg:mt-3"
            dataTest="breadcrumbs-cdp"
          />

          <Headline
            title={
              (contentful && contentful.fields.headline) || collection.title
            }
            shouldBeH1={!hasH1title}
            classNames="lg:mb-5 mb-1.5"
            extraElement={
              <span className="hidden lg:block">
                <ResultCounter productCount={visibleProducts.length} />
              </span>
            }
            smallerFont
          />

          {showPetSelector && !loading && (
            <PetProfileCollectionSelector
              location={location}
              temporarySelectedAllergies={temporarySelectedAllergies}
              setApplyPetProfile={setApplyPetProfile}
            />
          )}

          <ProductsGrid
            showFilter={true}
            contentfulData={contentful}
            shouldShowAllergyFilter={shouldShowAllergyFilter}
            setTemporarySelectedAllergies={setTemporarySelectedAllergies}
            additionalQuery={additionalQuery}
            shouldShowBlackRibbon={shouldShowBlackRibbon}
          />
        </div>
      </div>

      <div className="container mt-3 grid grid-cols-1 gap-5 lg:mt-8 lg:grid-cols-4">
        <div className="col-span-1" />
        <div className="col-span-1 lg:col-span-3">
          <ContentCmsContentStatic />
        </div>
      </div>
    </>
  );
};

export default Collection;
